import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  computed,
  inject,
} from '@angular/core'
import { FilterService } from '@progress/kendo-angular-grid'
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  FilterOperator,
} from '@progress/kendo-data-query'

import { SessionService } from '@hunt/services'

import { BaseCustomFilterComponent } from '../../../../../shared/components/custom-filters/base-custom-filter.component'
import { DashboardStateService } from '../../dashboard-state-service'

@Component({
  standalone: false,
  selector: 'dashboard-date-range-filter',
  styles: [
    `
      kendo-daterange > kendo-dateinput.range-filter {
        display: inline-block;
      }
      .k-button {
        margin-left: 5px;
      }
    `,
  ],
  template: `
    <kendo-daterange>
      <kendo-dateinput
        class="range-filter"
        kendoDateRangeStartInput
        [value]="startValue"
        (valueChange)="filterRange($event, endValue)"
      >
      </kendo-dateinput>
      <kendo-dateinput
        class="range-filter"
        kendoDateRangeEndInput
        [value]="endValue"
        (valueChange)="filterRange(startValue, $event)"
      >
      </kendo-dateinput>
    </kendo-daterange>
    @if (hasFilter) {
      <button
        kendoButton
        icon="clear"
        title="Clear"
        (click)="clearFilter()"
      ></button>
    }
  `,
})
export class DashboardDateRangeFilterComponent
  extends BaseCustomFilterComponent
  implements OnChanges
{
  dashboardStateService = inject(DashboardStateService)

  readonly session = inject(SessionService)
  readonly loaded = computed(
    () => !this.session.loading() && this.session.activeHunt() !== null,
  )
  @Input() public filter: CompositeFilterDescriptor
  @Input() public field: string

  public startValue: Date | null = null
  public endValue: Date | null = null

  // Constructor needed for proper filter functioning

  constructor() {
    const filterService = inject(FilterService, { optional: true })

    super(filterService)
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter'] || changes['field']) {
      const newStart = this.getDateFromFilter(FilterOperator.GreaterThanOrEqual)
      const newEnd = this.getDateFromFilter(FilterOperator.LessThanOrEqual)

      if (!datesEqual(this.startValue, newStart)) this.startValue = newStart
      if (!datesEqual(this.endValue, newEnd)) this.endValue = newEnd
    }
  }

  public resetDefault(): void {
    this.clearFilter()
  }

  public get hasFilter(): boolean {
    return this.filtersByField(this.field).length > 0
  }

  public clearFilter(): void {
    this.startValue = null
    this.endValue = null
    this.dashboardStateService.getState().filter.filters =
      this.dashboardStateService
        .getState()
        .filter.filters.filter((filter) => filter.field !== this.field)
  }

  public addFilter(filter: FilterDescriptor): void {
    this.dashboardStateService.getState().filter.filters.push({
      field: filter.field,
      operator: filter.operator,
      value: new Date(filter.value),
    })
  }

  public filterRange(start: Date, end: Date): void {
    this.startValue = start
    this.endValue = end

    const filters: FilterDescriptor[] = []

    if (start) {
      filters.push({
        field: this.field,
        operator: FilterOperator.GreaterThanOrEqual,
        value: start,
      })
    }

    if (end) {
      filters.push({
        field: this.field,
        operator: FilterOperator.LessThanOrEqual,
        value: end,
      })
    }

    if (filters.length) {
      this.dashboardStateService.getState().filter.filters.push(...filters)
    }
  }

  private getDateFromFilter(operator: FilterOperator): Date | null {
    if (this.filter && this.filter.filters) {
      const match = (this.filter.filters as FilterDescriptor[]).find(
        ({ field, operator: op }) => field === this.field && op === operator,
      )
      if (match && match.value) return new Date(match.value)
    }
    return null
  }
}

function datesEqual(a: Date | null, b: Date | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return a.getTime() === b.getTime()
}
